import { Component, ComponentInterface, Host, h, Prop } from "@stencil/core";
import { CallStateEnum } from "../../models";
import { store, Unsubscribe } from "@stencil/redux";
import { RootState } from "../../store/states";
import {
  DeviceService,
  MuteChangeRequestedAction,
  TranslateService,
  WindowEventService,
} from "../../services";
import { IMedia } from "../../models/IMedia";

@Component({
  tag: "app-voice-call",
  styleUrl: "app-voice-call.scss",
  shadow: true,
})
export class AppVoiceCall implements ComponentInterface {
  @Prop({ mutable: true }) $callState: CallStateEnum;
  @Prop({ mutable: true }) $isAgentMuted: boolean;
  @Prop({ mutable: true }) $isMicOn: boolean;
  @Prop({ mutable: true }) $mediaAvailable: IMedia;

  storeUnsubscribe: Unsubscribe;

  connectedCallback() {
    if (this.storeUnsubscribe) this.storeUnsubscribe();

    this.storeUnsubscribe = store.mapStateToProps(
      this,
      (rootState: RootState) => {
        const {
          call: { state, isAgentMuted, isMicOn, mediaAvailable },
        } = rootState;
        return {
          $callState: state,
          $isAgentMuted: isAgentMuted,
          $isMicOn: isMicOn,
          $mediaAvailable: mediaAvailable,
        };
      }
    );
  }

  disconnectedCallback() {
    this.storeUnsubscribe();
  }

  toggleMute() {
    WindowEventService.sendMessageToTarget(
      new MuteChangeRequestedAction("audio")
    );
  }

  get isDisplayCaptureVisible() {
    return (
      this.$mediaAvailable.displayCapture &&
      !this.isStarting &&
      !this.isRequestingFeedback &&
      !this.isRequestingConsent
    );
  }

  get isStarting() {
    return this.$callState === CallStateEnum.starting;
  }
  get isStarted() {
    return this.$callState === CallStateEnum.started;
  }
  get isOnHold() {
    return this.$callState === CallStateEnum.onHold;
  }
  get isRequestingConsent() {
    return this.$callState === CallStateEnum.consent;
  }
  get isRequestingFeedback() {
    return this.$callState === CallStateEnum.feedback;
  }

  render() {
    const onHoldText = TranslateService.instant("On hold");
    const connectingText = TranslateService.instant("Connecting...");
    const consentText = TranslateService.instant("Agree to continue");
    const micText = this.$isMicOn
      ? TranslateService.instant("Mute")
      : TranslateService.instant("Unmute");
    return (
      <Host>
        <av-agent class={{ "av-agent-mobile": DeviceService.isMobile }}>
          <div class="timer-container">
            {this.isStarting && (
              <span data-tid="voice/connecting">{connectingText}</span>
            )}
            {this.isRequestingConsent && (
              <span data-tid="voice/request-consent">{consentText}</span>
            )}
            {(this.isStarted || this.isOnHold) && (
              <av-timer data-tid="voice/timer"></av-timer>
            )}
          </div>
        </av-agent>
        <div>
          {this.isStarting && (
            <div class="spinner-container">
              <av-spinner isTray={true}></av-spinner>
            </div>
          )}
          {this.isStarted && (
            <div class="av-actions">
              {this.isDisplayCaptureVisible && (
                <div class="av-display-capture">
                  <app-display-capture></app-display-capture>
                </div>
              )}
              <div class="av-mic">
                <av-button
                  data-tid="audio/toggle-mute"
                  tooltip={micText}
                  shape="round"
                  color="accent"
                  onClick={this.toggleMute.bind(this)}
                >
                  {this.$isMicOn ? (
                    <av-asset image="mic"></av-asset>
                  ) : (
                    <av-asset image="mic-muted"></av-asset>
                  )}
                </av-button>
              </div>
            </div>
          )}
          {this.isOnHold && (
            <b data-tid="audio/on-hold" class="blink call-info">
              {onHoldText}
            </b>
          )}
        </div>
      </Host>
    );
  }
}
